import { get, post, put, del } from '../../../../GlobalModules/Utility/Http';

/**
 * Save bill posting settings to the backend (Create or Update based on existing data)
 * @param {Object} settingsData - Bill posting settings data
 * @returns {Promise<Object>} - API response
 */
export const saveBillPostingSettings = async (settingsData) => {
  try {
    // First check if settings already exist
    const existingSettings = await getBillPostingSettings();

    if (existingSettings.success && existingSettings.data?.slack) {
      // Settings exist, so update them
      return await updateBillPostingSettings(settingsData, existingSettings.data.slack);
    } else {
      // No existing settings, so create new ones
      return await createBillPostingSettings(settingsData);
    }
  } catch (error) {
    console.error('Error saving bill posting settings:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Failed to save bill posting settings',
      data: null
    };
  }
};

/**
 * Create new bill posting settings
 * @param {Object} settingsData - Bill posting settings data
 * @returns {Promise<Object>} - API response
 */
export const createBillPostingSettings = async (settingsData) => {
  try {
    const apiData = {
      api_key: settingsData.api_key,
      base_url: settingsData.base_url,
      room_list_endpoint: settingsData.room_list_endpoint,
      // guest_info_endpoint: settingsData.guest_info_endpoint,
      post_bill_endpoint: settingsData.post_bill_endpoint,
      // health_check_endpoint: settingsData.health_check_endpoint,
      acknowledgement_status_endpoint: settingsData.acknowledgement_status_endpoint,
      pms_property_name: settingsData.pms_property_name,
      store_id: localStorage.getItem("store_id")
    };

    const response = await post(`${process.env.EXTENDED_URL}bill_posting_settings/create`, apiData);

    if (response && response.data && response.data.success) {
      return {
        success: true,
        message: response.data.message || 'Bill posting settings created successfully',
        data: response.data.data
      };
    } else {
      return {
        success: false,
        message: response?.data?.message || 'Failed to create bill posting settings',
        data: null
      };
    }
  } catch (error) {
    console.error('Error creating bill posting settings:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Failed to create bill posting settings',
      data: null
    };
  }
};

/**
 * Get bill posting settings from the backend
 * @returns {Promise<Object>} - API response with settings data
 */
export const getBillPostingSettings = async () => {
  try {
    const queryParams = {
      store_id: localStorage.getItem("store_id")
    };

    const response = await get(`${process.env.EXTENDED_URL}bill_posting_settings/store`, queryParams);

    if (response && response.data && response.data.success) {
      return {
        success: true,
        message: response.data.message || 'Bill posting settings retrieved successfully',
        data: response.data.data
      };
    } else {
      return {
        success: false,
        message: response?.data?.message || 'Failed to retrieve bill posting settings',
        data: null
      };
    }
  } catch (error) {
    console.error('Error retrieving bill posting settings:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Failed to retrieve bill posting settings',
      data: null
    };
  }
};

/**
 * Test bill posting API connection
 * @param {Object} connectionData - Connection test data
 * @returns {Promise<Object>} - API response
 */
export const testBillPostingConnection = async (connectionData) => {
  try {
    const apiData = {
      api_key: connectionData.api_key,
      base_url: connectionData.base_url,
      health_check_endpoint: connectionData.health_check_endpoint
    };

    const response = await post(`${process.env.EXTENDED_URL}bill_posting_settings/test-connection`, apiData);

    if (response && response.data && response.data.success) {
      return {
        success: true,
        message: response.data.message || 'Connection test successful',
        data: response.data.data
      };
    } else {
      return {
        success: false,
        message: response?.data?.message || 'Connection test failed',
        data: null
      };
    }
  } catch (error) {
    console.error('Error testing bill posting connection:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Connection test failed',
      data: null
    };
  }
};

/**
 * Update bill posting settings
 * @param {Object} settingsData - Updated settings data
 * @param {string} slack - Slack identifier for the settings to update
 * @returns {Promise<Object>} - API response
 */
export const updateBillPostingSettings = async (settingsData, slack) => {
  try {
    const apiData = {
      api_key: settingsData.api_key,
      base_url: settingsData.base_url,
      room_list_endpoint: settingsData.room_list_endpoint,
      // guest_info_endpoint: settingsData.guest_info_endpoint,
      post_bill_endpoint: settingsData.post_bill_endpoint,
      // health_check_endpoint: settingsData.health_check_endpoint,
      acknowledgement_status_endpoint: settingsData.acknowledgement_status_endpoint,
      pms_property_name: settingsData.pms_property_name,
      category_endpoint: settingsData.category_endpoint,
      item_master_endpoint: settingsData.item_master_endpoint,
      update_inventory_endpoint: settingsData.update_inventory_endpoint
    };

    const response = await put(`${process.env.EXTENDED_URL}bill_posting_settings/${slack}`, apiData);

    if (response && response.data && response.data.success) {
      return {
        success: true,
        message: response.data.message || 'Bill posting settings updated successfully',
        data: response.data.data
      };
    } else {
      return {
        success: false,
        message: response?.data?.message || 'Failed to update bill posting settings',
        data: null
      };
    }
  } catch (error) {
    console.error('Error updating bill posting settings:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Failed to update bill posting settings',
      data: null
    };
  }
};

/**
 * Delete bill posting settings
 * @returns {Promise<Object>} - API response
 */
export const deleteBillPostingSettings = async () => {
  try {
    const queryParams = {
      settings_type: 'bill_posting_pms'
    };

    const response = await del(`${process.env.EXTENDED_URL}settings/bill_posting`, queryParams);

    if (response && response.data && response.data.success) {
      return {
        success: true,
        message: response.data.message || 'Bill posting settings deleted successfully',
        data: response.data.data
      };
    } else {
      return {
        success: false,
        message: response?.data?.message || 'Failed to delete bill posting settings',
        data: null
      };
    }
  } catch (error) {
    console.error('Error deleting bill posting settings:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Failed to delete bill posting settings',
      data: null
    };
  }
};

/**
 * Test rooms API connection with specific settings
 * @param {Object} connectionData - Connection test data
 * @returns {Promise<Object>} - API response
 */
export const testRoomsConnection = async (connectionData) => {
  try {
    // Build external API URL directly
    const externalUrl = `${connectionData.base_url}${connectionData.room_list_endpoint}`;

    // Format filters according to the API structure (empty for rooms list)
    const queryParams = {};

    // Custom headers for external API (same as fetchRooms)
    const customHeaders = {
      'x_ms_api_key': connectionData.api_key,
      'Content-Type': 'application/json',
    };

    // Add ngrok header if needed
    if (externalUrl.includes('ngrok')) {
      customHeaders['ngrok-skip-browser-warning'] = 'true';
    }

    const response = await get(externalUrl, queryParams, "global", customHeaders);

    if (!response || !response.data || !response.data.success) {
      return {
        success: false,
        message: response?.data?.message || 'Failed to fetch rooms',
        data: []
      };
    }

    return {
      success: true,
      message: response.data.message || 'Rooms fetched successfully',
      data: response.data.data || []
    };
  } catch (error) {
    console.error('Error testing rooms API connection:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Failed to fetch rooms',
      data: []
    };
  }
};

/**
 * Generate Room QR codes for PMS integration
 * @param {string|number} storeId - Store ID
 * @returns {Promise<Object>} - API response with room QR data
 */
export const generateRoomQr = async (storeId) => {
  try {
    const currentStoreId = storeId || localStorage.getItem("store_id");
    const response = await post(`${process.env.EXTENDED_URL}pms_integration/generate_room_qr`, {
      store_id: String(currentStoreId)
    });

    if (response && response.data && response.data.success) {
      return {
        success: true,
        message: response.data.message || 'Room QR codes generated successfully',
        data: response.data.data || []
      };
    } else {
      return {
        success: false,
        message: response?.data?.message || 'Failed to generate room QR codes',
        data: []
      };
    }
  } catch (error) {
    console.error('Error generating room QR codes:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Failed to generate room QR codes',
      data: []
    };
  }
};
